import React, { useState } from "react";
import { useAppContext } from "../../../contexts/AppContext";
import Dialog from "carbon-react/lib/components/dialog";
import Heading from "carbon-react/lib/components/heading";
import Content from "carbon-react/lib/components/content";
import TextArea from "carbon-react/lib/__experimental__/components/textarea";
import ErrorMessage from "../../messages/ErrorMessage";
import LanguageSelector from "../../common/languageSelector/LanguageSelector";
import DialogConfirmButtons from "../../common/dialogConfirmButtons/DialogConfirmButtons";
import ConfirmDialog from "../../common/confirmDialog/ConfirmDialog";
import { useUserApi } from "../../../services/UserApi";
import StepTitle from "../../common/stepTitle/StepTitle";
import StepHeading from "../../common/stepHeading/StepHeading";
import { isAValidEmailAddress } from "../../../constants/validation";
import Textbox from "carbon-react/lib/__experimental__/components/textbox";

const initialState = {
  languageCode: null,
  validLanguageCode: true,
  agentComments: "",
  validAgentComments: true,
  email: "",
  validEmail: true,
  emailValidationMsg: "",
  confirmEmail: "",
  validConfirmEmail: true,
  confirmEmailValidationMsg: "",
};

const BypassEmailVerificationDialog = ({ open, user, onClose }) => {
  const { bypassEmailVerification } = useUserApi();
  const [errorMsg, setErrorMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [languageCode, setLanguageCode] = useState(initialState.languageCode);
  const [validLanguageCode, setValidLanguageCode] = useState(
    initialState.validLanguageCode
  );
  const [agentComments, setAgentComments] = useState(
    initialState.agentComments
  );
  const [validAgentComments, setValidAgentComments] = useState(
    initialState.validAgentComments
  );
  const [email, setEmail] = useState(initialState.email);
  const [validEmail, setValidEmail] = useState(initialState.validEmail);
  const [emailValidationMsg, setEmailValidationMessage] = useState(
    initialState.emailValidationMsg
  );
  const [confirmEmail, setConfirmEmail] = useState(initialState.confirmEmail);
  const [validConfirmEmail, setValidConfirmEmail] = useState(
    initialState.validConfirmEmail
  );
  const [
    confirmEmailValidationMsg,
    setConfirmEmailValidationMessage,
  ] = useState(initialState.confirmEmailValidationMsg);
  const [busy, setBusy] = useState(false);
  const { toastSuccess } = useAppContext();
  const [
    confirmBypassEmailVerificationOpen,
    setConfirmBypassEmailVerificationOpen,
  ] = useState(false);

  const showDialogError = (msg) => {
    setErrorMsg(msg);
    setShowError(true);
  };

  const clearDialogError = () => {
    setErrorMsg("");
    setShowError(false);
  };

  const setEmailValid = (valid, validationMsg = "") => {
    setValidEmail(valid);
    setEmailValidationMessage(validationMsg);
  };

  const setConfirmEmailValid = (valid, validationMsg = "") => {
    setValidConfirmEmail(valid);
    setConfirmEmailValidationMessage(validationMsg);
  };

  const validateEmail = () => {
    if (!isAValidEmailAddress(email)) {
      setEmailValid(false, "Please enter a valid email");
      return false;
    }

    if (email === user?.email.trim()) {
      setEmailValid(false, "This is the user's current email");
      return false;
    }

    if (!validConfirmEmail) validateConfirmEmail();

    setValidEmail(true);
    return true;
  };

  const validateConfirmEmail = () => {
    if (!isAValidEmailAddress(confirmEmail)) {
      setConfirmEmailValid(false, "Please enter a valid email");
      return false;
    }

    if (!email || email !== confirmEmail) {
      setConfirmEmailValid(false, "Values do not match");
      return false;
    }

    setValidConfirmEmail(true);
    return true;
  };

  const validateAgentComments = () => {
    let valid = true;

    if (agentComments.length === 0) {
      valid = false;
    }

    setValidAgentComments(valid);
    return valid;
  };

  const validateLanguageCode = () => {
    let valid = languageCode && languageCode.length > 0;
    setValidLanguageCode(valid);
    return valid;
  };

  const validateInputs = () => {
    let valid = true;
    if (!validateLanguageCode()) valid = false;
    if (!validateAgentComments()) valid = false;
    if (!validateEmail()) valid = false;
    if (!validateConfirmEmail()) valid = false;
    return valid;
  };

  const onDialogBypassEmailVerification = () => {
    if (!validateInputs()) return;
    setConfirmBypassEmailVerificationOpen(true);
  };

  const onConfirmBypassEmailVerificationClose = () => {
    setConfirmBypassEmailVerificationOpen(false);
  };

  const onConfirmBypassEmailVerificationContinue = async () => {
    setConfirmBypassEmailVerificationOpen(false);
    await performBypassEmailVerification();
  };

  const performBypassEmailVerification = async () => {
    if (busy) return;

    try {
      clearDialogError();
      setBusy(true);

      var apiResult = await bypassEmailVerification(
        user.userId,
        email,
        languageCode,
        agentComments
      );

      if (!apiResult?.success)
        throw new Error(
          apiResult?.error?.description ||
            "The bypass email verification operation failed"
        );

      toastSuccess(
        `User email verification successfully bypassed - '${user.email}'`
      );
      onClose();
      resetDialogState();
    } catch (error) {
      showDialogError(error.toString());
      setConfirmBypassEmailVerificationOpen(false);
    }
    setBusy(false);
  };

  const resetDialogState = () => {
    clearDialogError();
    setConfirmBypassEmailVerificationOpen(false);
    setLanguageCode(initialState.languageCode);
    setValidLanguageCode(initialState.validLanguageCode);
    setAgentComments(initialState.agentComments);
    setValidAgentComments(initialState.validAgentComments);
    setEmail(initialState.email);
    setValidEmail(initialState.validEmail);
    setEmailValidationMessage(initialState.emailValidationMsg);
    setConfirmEmail(initialState.confirmEmail);
    setValidConfirmEmail(initialState.validConfirmEmail);
    setConfirmEmailValidationMessage(initialState.confirmEmailValidationMsg);
  };

  const onDialogCancel = () => {
    resetDialogState();
    onClose();
  };

  const onEmailChanged = (email) => {
    const sanitizedEmail = email && email.length > 0 ? email.trim() : "";
    setEmail(sanitizedEmail);
    setValidEmail(true);
  };

  const onConfirmEmailChanged = (email) => {
    const sanitizedEmail = email && email.length > 0 ? email.trim() : "";
    setConfirmEmail(sanitizedEmail);
    setValidConfirmEmail(true);
  };

  const agentCommentsValidationAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: "Please indicate why this action is being performed.",
  };

  const languageSelection = (
    <LanguageSelector
      required={false}
      appearBusy={busy}
      value={languageCode}
      onValueChanged={(newLanguage) => {
        setLanguageCode(newLanguage);
      }}
      onBlur={validateLanguageCode}
      appearInvalid={!validLanguageCode}
      validationMsg="Language must be provided"
    />
  );

  const confirmBypassEmailVerification = (
    <ConfirmDialog
      open={confirmBypassEmailVerificationOpen}
      prompt="This action is irreversible, are you certain you want to bypass email verification for this user?"
      confirmBtnLabel="Confirm"
      onConfirm={onConfirmBypassEmailVerificationContinue}
      onCancel={onConfirmBypassEmailVerificationClose}
    />
  );

  const agentCommentsInput = (
    <Content title="Comments">
      <TextArea
        name="bypass-email-verification-agent-comments"
        disabled={busy}
        value={agentComments}
        characterLimit={"250"}
        enforceCharacterLimit={true}
        placeholder={"Please indicate why this action is being performed."}
        onChange={(e) => {
          setValidAgentComments(true);
          setAgentComments(e.target.value);
        }}
        onBlur={validateAgentComments}
        {...(!validAgentComments ? agentCommentsValidationAttributes : {})}
      />
    </Content>
  );

  const dialogButtons = (
    <DialogConfirmButtons
      appearBusy={busy}
      confirmBtnLabel={"Bypass email verification"}
      onConfirm={onDialogBypassEmailVerification}
      onCancel={onDialogCancel}
    />
  );

  const emailWarningAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: emailValidationMsg,
  };

  const confirmEmailWarningAttributes = {
    hasWarning: true,
    inputIcon: "warning",
    tooltipMessage: confirmEmailValidationMsg,
  };

  const emailInput = (
    <Textbox
      id="bypass-email-verification-new-email"
      autoFocus
      label="New email address"
      disabled={busy}
      value={email}
      onChange={(e) => {
        onEmailChanged(e.target.value);
      }}
      onBlur={validateEmail}
      {...(!validEmail ? emailWarningAttributes : {})}
    />
  );

  const confirmEmailInput = (
    <Textbox
      id="bypass-email-verification-confirm-email"
      label="Confirm new email address"
      disabled={busy}
      value={confirmEmail}
      onChange={(e) => {
        onConfirmEmailChanged(e.target.value);
      }}
      onBlur={validateConfirmEmail}
      {...(!validConfirmEmail ? confirmEmailWarningAttributes : {})}
    />
  );

  const errorMessage = (
    <>
      {showError !== true ? null : (
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed!"
          }
        />
      )}
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        showCloseIcon={true}
        onClose={onDialogCancel}
        onCancel={onDialogCancel}
      >
        <Heading title="Bypass email verification" />
        <StepTitle title="Bypassing email verification allows the user to log in without verifying their email address." />
        <StepHeading label="Email address" value={user?.email} />
        {emailInput}
        {confirmEmailInput}
        {languageSelection}
        {agentCommentsInput}
        {dialogButtons}
        {errorMessage}
      </Dialog>
      {confirmBypassEmailVerification}
    </>
  );
};

export default BypassEmailVerificationDialog;
